import { useState } from 'react'
import { Screen, Panel, HeroLens } from '../components/ui.jsx'
import { Building, User, ArrowRight } from '../components/icons.jsx'
import { BUILD_001_FIXTURE_LOCK } from '../data/adapter.js'
import './entry.css'

/*
 * Entry Point Selection — selects the entry EXPERIENCE, not identity (identity
 * is resolved later). Scoped to what's real: Owner → Sign In (out of scope
 * here), Team Member → Join a Team → Check In. Pattern supports adding
 * Customer/Vendor later without redesign.
 *
 * BUILD 002 HOTFIX (U-26). The owner board was reachable only via ?dev=owner,
 * and the artifact host serving the Founder preview does not pass a query
 * string through to the app — so on a real phone the board could not be opened
 * at all, and the Owner card explained that owner sign-in lives elsewhere.
 *
 * While the fixture lock is on, that card opens the dev-only board directly and
 * says plainly that is what it does. This is NOT a sign-in and must never read
 * as one: there is no authentication anywhere in Flow yet. With the lock off it
 * behaves exactly as before, so nothing changes for a real build.
 */
export default function EntryPoint({ onTeamMember, onOwner }) {
  const [note, setNote] = useState(false)
  const devOwner = BUILD_001_FIXTURE_LOCK && typeof onOwner === 'function'
  return (
    <Screen>
      {/* The title welcomes; the two cards explain themselves. No subtitle, no
          question, no time-of-day logic — "Welcome to Flow" works at any time,
          anywhere (UX refinement from real product testing). */}
      <div className="stack" style={{ marginTop: 'var(--sp-4)' }}>
        <h1 className="title-xl rise" style={{ animationDelay: '80ms' }}>
          Welcome to Flow
        </h1>
      </div>

      <div className="entry-options">
        <Panel as="button" className="entry-card rise" style={{ animationDelay: '300ms' }}
               onClick={onTeamMember} aria-label="Team Member — join your team and check in">
          <HeroLens size={52}><User size={22} /></HeroLens>
          <span className="entry-card__text">
            <span className="entry-card__title">Team Member</span>
            <span className="entry-card__sub">Join your team & check in</span>
          </span>
          <ArrowRight size={22} />
        </Panel>

        <Panel as="button"
               className={`entry-card rise${devOwner ? '' : ' entry-card--muted'}`}
               style={{ animationDelay: '380ms' }}
               onClick={devOwner ? onOwner : () => setNote(true)}
               aria-label={devOwner
                 ? 'Owner board — development only, fixture data, no sign-in'
                 : 'Business Owner — sign in'}>
          <HeroLens size={52}><Building size={22} /></HeroLens>
          <span className="entry-card__text">
            <span className="entry-card__title">
              {devOwner ? 'Owner / Superintendent' : 'Business Owner'}
              {devOwner && <span className="entry-card__devtag">DEV ONLY</span>}
            </span>
            <span className="entry-card__sub">
              {devOwner
                ? 'Workforce board — fixture data, no sign-in'
                : 'Sign in to your workspace'}
            </span>
          </span>
          <ArrowRight size={22} />
        </Panel>

        {note && (
          <p className="meta rise" role="status">
            Owner sign-in lives in a separate part of Flow — this build is the Team Member check-in.
          </p>
        )}
      </div>
    </Screen>
  )
}
